if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(a
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]:not(.variant-gallery-hidden)');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition || 1);
      }

      filterForVariant(variant) {
        if (!variant) return;

        const optionIndex = Number(this.dataset.variantOptionIndex || 0);
        const optionValue = variant.options?.[optionIndex];
        const featuredMediaId = variant.featured_media?.id;
        const featuredMediaItem = featuredMediaId
          ? this.elements.viewer.querySelector(`[data-media-id$="-${featuredMediaId}"]`)
          : null;
        const featuredGroup = featuredMediaItem?.dataset.variantGroup;
        const selectedGroup =
          featuredGroup && featuredGroup !== 'shared' ? featuredGroup : this.handleize(optionValue);

        if (!selectedGroup) return;

        const groupedItems = this.querySelectorAll('[data-variant-group]');
        if (!groupedItems.length) return;

        groupedItems.forEach((item) => {
          const itemGroup = item.dataset.variantGroup;
          const shouldShow = itemGroup === selectedGroup || itemGroup === 'shared';

          item.classList.toggle('variant-gallery-hidden', !shouldShow);
          item.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        });

        // Keep visible items consecutive in the DOM. The customized Haroutine
        // gallery uses child-position CSS, so leaving hidden Jar/Packets items
        // interleaved can make the visible set look like every other image.
        const reorderByVisibility = (list) => {
          if (!list) return;

          const items = Array.from(list.children).filter((item) => item.dataset.variantGroup);
          const visibleItems = items.filter((item) => !item.classList.contains('variant-gallery-hidden'));
          const hiddenItems = items.filter((item) => item.classList.contains('variant-gallery-hidden'));

          [...visibleItems, ...hiddenItems].forEach((item) => list.append(item));
        };

        reorderByVisibility(this.elements.viewer.querySelector('ul'));
        reorderByVisibility(this.elements.thumbnails?.querySelector('ul'));

        const visibleMedia = Array.from(
          this.elements.viewer.querySelectorAll(
            '[data-media-id]:not(.variant-gallery-hidden):not([data-variant-main-clone])'
          )
        );

        if (!visibleMedia.length) return;

        const activeMedia = this.elements.viewer.querySelector(
          '[data-media-id].is-active:not(.variant-gallery-hidden)'
        );

        if (!activeMedia) {
          this.setActiveMedia(visibleMedia[0].dataset.mediaId, false);
        }

        const currentCounter = this.querySelector('.slider-counter--current');
        const totalCounter = this.querySelector('.slider-counter--total');

        if (currentCounter) currentCounter.textContent = '1';
        if (totalCounter) totalCounter.textContent = String(visibleMedia.length);

        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      handleize(value) {
        const handle = String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');

        // Accept common Haroutine labels such as "15 Packets",
        // "Packet", "Sachets", "30-serving Jar", or "Tub".
        if (/(^|-)(packet|packets|sachet|sachets|stick|sticks)(-|$)/.test(handle)) return 'packets';
        if (/(^|-)(jar|jars|tub|tubs)(-|$)/.test(handle)) return 'jar';

        return handle;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
